// Today's Announcements (AdminToday.dc.html): what's showing at the top of members' libraries, with
// Take it down, and the composer behind "Post 1". The board's composer has 1 box, 2 audience chips and
// a typed date; the Library's card shows a headline, a line and a button (hub.announcements), so the
// composer adds an optional line and button, a product picker for "Owners of a product" and a date
// picked rather than typed (the stage 5 critic's batch). Owner and admin post and take down (row
// level security); support and hosts see what's showing.
//
// Direct writes under row level security (data-model 10.4.2): an insert into hub.announcements, and an
// update setting taken_down_at; the audit trigger records announcement_posted and
// announcement_taken_down with the caller.
import { useState } from 'react'
import { Button, DateField, Dot, Field, Loading, Problem, Select, TextInput, productDot } from '../ui/index.js'
import { addDays, todayInUk, ukToIso } from '../ui/format.js'
import { AUDIENCE_WORDS, announcementMeta, buttonPages, productTitle } from './words.js'

export const ANNOUNCEMENT_COLUMNS = 'id, headline, body_line, button_label, button_path, audience, product_id, starts_at, comes_down_at, created_by, created_at'

// Showing now, or still to come down: not taken down, and not past its date
export const stillUp = (a, nowMs) => !a.comes_down_at || Date.parse(a.comes_down_at) > nowMs

// The products an announcement can be for: the paid ones live in the hub (never a 1-2-1 space)
export const audienceProducts = products => products.filter(p => p.access_kind === 'paid' && p.moved_in && p.state === 'live' && p.type !== 'client_space')

const AUDIENCES = [
  { key: 'everyone', label: AUDIENCE_WORDS.everyone },
  { key: 'product_owners', label: AUDIENCE_WORDS.product_owners },
]

export function Announcements({ load, setList, api, role, personId, products, nowMs }) {
  const canPost = role === 'owner' || role === 'admin'
  const [composing, setComposing] = useState(false)
  const [downError, setDownError] = useState(null)
  const [downBusy, setDownBusy] = useState(null)
  const showing = (load.data || []).filter(a => stillUp(a, nowMs))

  async function takeDown(a) {
    setDownBusy(a.id)
    setDownError(null)
    try {
      const rows = await api.run(api.from('announcements').update({ taken_down_at: new Date().toISOString(), taken_down_by: personId })
        .eq('id', a.id).is('taken_down_at', null).select('id'))
      if (!rows?.length) throw new api.AdminError('not_there', 0, api.WORDS.notThere)
      setList(list => list.filter(x => x.id !== a.id))
    } catch (e) {
      setDownError({ id: a.id, message: api.words(e) })
    } finally {
      setDownBusy(null)
    }
  }

  return (
    <section className="td-card td-side" aria-labelledby="td-news-h">
      <div className="td-side-head">
        <h2 className="td-h2 td-h2--side" id="td-news-h">Announcements</h2>
        {canPost && (
          <button type="button" className="td-small-btn" aria-expanded={composing} onClick={() => setComposing(c => !c)}>
            {composing ? 'Close' : 'Post 1'}
          </button>
        )}
      </div>
      {load.status === 'loading' && !load.data && <Loading className="td-flush" />}
      {load.status === 'error' && !load.data && <Problem error={load.error} onRetry={load.reload} />}
      {showing.map(a => (
        <div key={a.id} className="td-news">
          <span className="td-news-head">{a.headline}</span>
          {a.body_line && <span className="td-news-line">{a.body_line}</span>}
          {a.button_label && <span className="td-news-line">Button: {a.button_label}</span>}
          <span className="td-news-meta">{announcementMeta(a, products, nowMs)}</span>
          {canPost && (
            <button type="button" className="td-tiny-btn" disabled={downBusy === a.id} aria-busy={downBusy === a.id || undefined}
              onClick={() => takeDown(a)}>Take it down</button>
          )}
          {downError?.id === a.id && <span className="ad-error" role="alert">{downError.message}</span>}
        </div>
      ))}
      {load.data && !showing.length && <span className="td-side-line">Nothing showing. Members' libraries have no announcement.</span>}
      {canPost && composing && (
        <Composer api={api} personId={personId} products={products} nowMs={nowMs}
          onPosted={row => { setList(list => [row, ...(list || [])]); setComposing(false) }} />
      )}
    </section>
  )
}

function Composer({ api, personId, products, onPosted, nowMs }) {
  const [headline, setHeadline] = useState('')
  const [line, setLine] = useState('')
  const [buttonLabel, setButtonLabel] = useState('')
  const [buttonPath, setButtonPath] = useState('')
  const [audience, setAudience] = useState('everyone')
  const [productId, setProductId] = useState('')
  const [until, setUntil] = useState('')
  // once Post it has been pressed, every field says what's wrong with it as it's typed
  const [tried, setTried] = useState(false)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState(null)
  const choices = audienceProducts(products)
  const pages = buttonPages(products)
  const tomorrow = addDays(todayInUk(nowMs), 1)

  function check() {
    const e = {}
    const h = headline.trim()
    if (!h) e.headline = 'Type what members see first.'
    else if (h.length > 200) e.headline = 'Keep it to 200 characters.'
    if (line.trim().length > 400) e.line = 'Keep the line to 400 characters.'
    if (buttonLabel.trim() && !buttonPath) e.buttonPath = 'Pick the page the button opens.'
    if (buttonPath && !buttonLabel.trim()) e.buttonLabel = 'Give the button its words.'
    if (audience === 'product_owners' && !productId) e.product = 'Pick the product.'
    if (until && until < tomorrow) e.until = 'Pick a day after today.'
    return e
  }

  const errors = tried ? check() : {}

  async function post() {
    setTried(true)
    setProblem(null)
    if (Object.keys(check()).length) return
    setBusy(true)
    try {
      const row = {
        headline: headline.trim(),
        body_line: line.trim() || null,
        button_label: buttonLabel.trim() || null,
        button_path: buttonPath || null,
        audience,
        product_id: audience === 'product_owners' ? productId : null,
        // it shows to the end of the day picked, and comes down at the start of the next
        comes_down_at: until ? ukToIso(addDays(until, 1), '00:00') : null,
        created_by: personId,
      }
      const rows = await api.run(api.from('announcements').insert(row).select(ANNOUNCEMENT_COLUMNS))
      onPosted(rows[0])
    } catch (err) {
      setProblem(api.words(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="td-compose" role="group" aria-label="Post an announcement">
      <textarea className={`td-compose-text${errors.headline ? ' td-wrong' : ''}`} aria-label="Announcement" aria-invalid={errors.headline ? true : undefined}
        placeholder="What members see at the top of their library" value={headline} onChange={ev => setHeadline(ev.target.value)} />
      {errors.headline && <span className="ad-error" role="alert">{errors.headline}</span>}
      <Field label="A line under it, if you want 1" error={errors.line} plain className="td-compose-field">
        {props => <TextInput {...props} value={line} onChange={setLine} />}
      </Field>
      <div className="td-compose-pair">
        <Field label="A button, if you want 1" error={errors.buttonLabel} plain className="td-compose-field">
          {props => <TextInput {...props} value={buttonLabel} onChange={setButtonLabel} placeholder="See free webinars" />}
        </Field>
        <Field label="It opens" error={errors.buttonPath} plain className="td-compose-field">
          {props => <Select {...props} value={buttonPath} onChange={setButtonPath} placeholder="Pick a page" options={pages} />}
        </Field>
      </div>
      <span className="td-compose-label" id="td-aud-label">Who sees it</span>
      <div className="td-chips" role="radiogroup" aria-labelledby="td-aud-label">
        {AUDIENCES.map(a => (
          <button key={a.key} type="button" role="radio" aria-checked={audience === a.key} className="td-aud"
            onClick={() => setAudience(a.key)}>{a.label}</button>
        ))}
      </div>
      {audience === 'product_owners' && (
        <>
          <span className="td-compose-label" id="td-aud-product">Which product</span>
          <div className="td-chips" role="radiogroup" aria-labelledby="td-aud-product">
            {choices.map(p => (
              <button key={p.id} type="button" role="radio" aria-checked={productId === p.id} className="td-aud"
                onClick={() => setProductId(p.id)}>
                <Dot colour={productDot(p)} size={7} />{productTitle({ ...p, product_name: p.name, product_type: p.type })}
              </button>
            ))}
          </div>
          {errors.product && <span className="ad-error" role="alert">{errors.product}</span>}
        </>
      )}
      <DateField label="Comes down on" value={until} onChange={setUntil} min={tomorrow} plain error={errors.until}
        hint={until ? undefined : 'Leave it empty and it stays until you take it down.'} />
      {problem && <span className="ad-error" role="alert">{problem}</span>}
      <Button kind="ink" size="sm" className="td-post" busy={busy} busyLabel="Posting" onClick={post}>Post it</Button>
    </div>
  )
}

// What Today reads: the announcements not taken down, newest first
export const readAnnouncements = api => api.run(api.from('announcements').select(ANNOUNCEMENT_COLUMNS)
  .is('taken_down_at', null).order('starts_at', { ascending: false }).limit(20))
